import * as XLSX from 'xlsx';

const num = (value) => Number(value) || 0;
const paymentStatusLabel = (status) => status === 'Vencida' ? 'Vencido' : status;

function nfRows(notas) {
  return (notas || []).flatMap((nf) => {
    const lineas = nf.lineas?.length ? nf.lineas : [{}];
    return lineas.map((ln) => ({
      'Proveedor': nf.proveedor,
      'N° NF': nf.nro_nf,
      'Fecha NF': nf.fecha_nf,
      'OC': nf.oc,
      'Grado': ln.grado || '',
      'Espesor': ln.espesor || '',
      'Ancho': ln.ancho || '',
      'Largo': ln.largo || '',
      'Almacén': nf.almacen,
      'Tipo movimiento': nf.tipo_movimiento,
      'Producto': nf.producto,
      'Paquetes': ln.paquetes || 0,
      'Pcs/paq': ln.pcs_paq || 0,
      'Total pcs': ln.total_pcs || 0,
      'M3 línea': num(ln.m3),
      'R$/m3': num(ln.brl_m3),
      'USD/m3': num(ln.usd_m3),
      'TC': num(ln.tc),
      'Total NF (R$)': num(nf.total_nf_brl),
      'Estado de pago': paymentStatusLabel(nf.estado_pago),
      'Fecha vencimiento': nf.fecha_vencimiento,
      'Días vencidos': nf.dias_vencidos ?? '',
      'Fecha pago': nf.fecha_pago || '',
    }));
  });
}

function appendSheet(workbook, rows, name) {
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Información: 'Sin registros' }]);
  const widths = Object.keys(rows[0] || { Información: '' }).map((key) => ({
    wch: Math.min(42, Math.max(12, key.length + 2)),
  }));
  sheet['!cols'] = widths;
  XLSX.utils.book_append_sheet(workbook, sheet, name);
}

export function buildEstadoCuentaWorkbook({ notas, solicitudes = [], descuentos = [] }) {
  const workbook = XLSX.utils.book_new();
  appendSheet(workbook, nfRows(notas), 'Estado de cuenta');
  appendSheet(workbook, solicitudes.map((row) => ({
    'Solicitud': row.correlativo,
    'Fecha': row.fecha_generacion,
    'Proveedor': row.proveedor,
    'M3': num(row.total_m3),
    'Total NF (R$)': num(row.monto_bruto_brl),
    'Descuentos (R$)': num(row.descuentos_brl),
    'Neto solicitado (R$)': num(row.monto_neto_brl),
    'Estado': row.estado,
    'Usuario': row.usuario,
  })), 'Solicitudes');
  appendSheet(workbook, descuentos.map((row) => ({
    'Correlativo': row.correlativo,
    'Tipo': row.tipo,
    'Proveedor responsable': row.proveedor_responsable,
    'Fecha origen': row.fecha_origen,
    'Proveedor extracosto': row.proveedor_extracosto,
    'Invoice': row.nro_invoice,
    'BL': row.nro_bl,
    'Booking': row.nro_booking,
    'M3': num(row.m3),
    'Piezas': row.piezas || 0,
    'Valor': num(row.valor),
    'Moneda': row.moneda,
    'Estado': row.es_borrador ? 'Borrador' : row.estado,
    'Fecha aplicación': row.fecha_aplicacion || '',
  })), 'Extracostos');
  return workbook;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportControlExcel(data, filename = 'Control_pago_proveedores.xlsx') {
  const workbook = buildEstadoCuentaWorkbook(data);
  const array = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  downloadBlob(
    new Blob([array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}

const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;');

const fmt = (value, decimals = 2) => new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals,
}).format(num(value));

function wrapBase64(value) {
  return String(value || '').match(/.{1,76}/g)?.join('\r\n') || '';
}

function utf8Base64(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function buildSolicitudPagoEml({ solicitud, solicitudes, descuentos }) {
  const notas = solicitud.notas_fiscales || [];
  const workbook = buildEstadoCuentaWorkbook({
    notas: solicitud.estado_cuenta_proveedor || notas,
    solicitudes,
    descuentos,
  });
  const excelBase64 = XLSX.write(workbook, { bookType: 'xlsx', type: 'base64' });
  const nfLabels = notas.map((nf) => nf.nro_nf).join(', ');
  const condicion = solicitud.motivo || solicitud.condicion_pago || 'Pago a proveedor';
  const subject = `Transferencia desde PTB a ${solicitud.proveedor} - NF ${nfLabels} por R$ ${fmt(solicitud.monto_neto_brl)} - ${condicion}`;
  const filas = notas.flatMap((nf) => (nf.lineas?.length ? nf.lineas : [{}]).map((ln) => `
    <tr>
      <td>${escapeHtml(nf.oc)}</td><td>${escapeHtml(nf.fecha_nf)}</td><td>${escapeHtml(nf.nro_nf)}</td>
      <td>${escapeHtml(ln.grado)}</td><td>${escapeHtml(ln.espesor)}</td><td>${escapeHtml(ln.ancho)}</td><td>${escapeHtml(ln.largo)}</td>
      <td>${escapeHtml(ln.paquetes || 0)}</td><td>${escapeHtml(ln.pcs_paq || 0)}</td><td>${fmt(ln.brl_m3)}</td>
      <td>${fmt(ln.usd_m3)}</td><td>${fmt(ln.tc, 4)}</td><td>${escapeHtml(ln.total_pcs || 0)}</td><td>${fmt(ln.m3, 3)}</td>
      <td>${fmt(ln.total_brl)}</td>
    </tr>`)).join('');
  const html = `<!doctype html><html><body style="font-family:Arial,sans-serif;color:#17211d">
    <p>Estimados,</p><p>Se solicita gestionar la siguiente transferencia:</p>
    <table cellpadding="5" cellspacing="0" border="1" style="border-collapse:collapse;font-size:11px">
      <thead style="background:#086b56;color:#fff"><tr><th>OC</th><th>Fecha NF</th><th>NF</th><th>Grado</th><th>Espesor</th><th>Ancho</th><th>Largo</th><th>Paquetes</th><th>Pcs/paq</th><th>R$/m3</th><th>USD/m3</th><th>TC</th><th>Total pcs.</th><th>Total m3</th><th>Total R$</th></tr></thead>
      <tbody>${filas}</tbody>
      <tfoot style="font-weight:bold"><tr><td colspan="12">Totales</td><td>${notas.flatMap((nf) => nf.lineas || []).reduce((s, ln) => s + num(ln.total_pcs), 0)}</td><td>${fmt(solicitud.total_m3, 3)}</td><td>${fmt(solicitud.monto_bruto_brl)}</td></tr></tfoot>
    </table>
    ${num(solicitud.descuentos_brl) ? `<p>Descuentos aplicados: R$ ${fmt(solicitud.descuentos_brl)}<br><strong>Valor neto solicitado: R$ ${fmt(solicitud.monto_neto_brl)}</strong></p>` : ''}
    <p>Se adjunta el estado de cuenta histórico del proveedor.</p>
    <p>Saludos.</p></body></html>`;
  const boundary = `----PalmTradeBoundary${Date.now()}`;
  const filename = `Estado_cuenta_${String(solicitud.proveedor || 'proveedor').replace(/[^a-z0-9]+/gi, '_')}.xlsx`;
  return [
    'X-Unsent: 1',
    `To: ${solicitud.correo_proveedor || ''}`,
    `Subject: =?UTF-8?B?${utf8Base64(subject)}?=`,
    'MIME-Version: 1.0',
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
    '', `--${boundary}`,
    'Content-Type: text/html; charset="UTF-8"',
    'Content-Transfer-Encoding: base64',
    '', wrapBase64(utf8Base64(html)),
    '', `--${boundary}`,
    `Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; name="${filename}"`,
    'Content-Transfer-Encoding: base64',
    `Content-Disposition: attachment; filename="${filename}"`,
    '', wrapBase64(excelBase64),
    '', `--${boundary}--`, '',
  ].join('\r\n');
}

export function generarCorreoOutlookConExcel({ solicitud, solicitudes, descuentos }) {
  const eml = buildSolicitudPagoEml({ solicitud, solicitudes, descuentos });
  downloadBlob(new Blob([eml], { type: 'message/rfc822;charset=utf-8' }), `${solicitud.correlativo}.eml`);
}

export { nfRows };
